/*
 * Copyright (c) 2023 Bolmate B.V.
 */
import './style/adminConnections.scss';
import {ChangeFunction, DVRDSwitch, Loader} from '@dvrd/dvr-controls';

import React from 'react';
import {useTranslation} from 'react-i18next';

import Connection from '../../../models/connectionModel';

import classNames from 'classnames';

import {ConnectionStatus} from '../../../util/interfaces';

import IDate from '@dvrd/idate';

import {getMaintenanceMode} from '../../../util/maintenanceUtil';

interface Props {
    onToggleMaintenance: ChangeFunction<boolean>;
    connections: Array<Connection>;
    loading: boolean;
}

export default function AdminConnections(props: Props) {
    const {t} = useTranslation();
    const {onToggleMaintenance, connections, loading} = props;

    function getLastChanged(connection: Connection): string {
        const {statusChangedAtDate} = connection;
        if (!statusChangedAtDate) return t('admin.connections.na', 'n.v.t.');
        const now = IDate.now();
        const format = now.isAt(statusChangedAtDate, 'd') ? 'HH:mm' : 'DD-MM-YYYY HH:mm';
        return statusChangedAtDate.format(format);
    }

    function renderMaintenanceToggle() {
        return (
            <DVRDSwitch
                onChange={onToggleMaintenance}
                value={getMaintenanceMode()}
                labels={[t('admin.connections.maintenance', 'Onderhoud'), '']}
                className='maintenance-switch'
            />
        );
    }

    function renderConnection(connection: Connection, index: number) {
        const statusMap: Record<ConnectionStatus, [string, string]> = {
            [ConnectionStatus.UP]: ['up', t('admin.connections.status.up', 'Up')],
            [ConnectionStatus.UNSTABLE]: ['unstable', t('admin.connections.status.unstable', 'Instabiel')],
            [ConnectionStatus.DOWN]: ['down', t('admin.connections.status.down', 'Down')]
        };
        const [status, title] = statusMap[connection.status];
        return (
            <div className={classNames('connection', status)} key={index}>
                <div className='connection-indicator' title={title} />
                <span className='connection-label'>{connection.translatedName}</span>
                <label className='checked-label'>
                    {t('admin.connections.lastChanged', 'Laatst gewijzigd')}: {getLastChanged(connection)}
                </label>
            </div>
        );
    }

    return (
        <div className='connections-container'>
            <Loader active={loading} />
            {renderMaintenanceToggle()}
            <label className='connections-label'>{t('admin.connections.title', 'Verbindingen')}</label>
            <div className='admin-connections'>{connections.map(renderConnection)}</div>
        </div>
    );
}
